import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { 
  useGetMe, 
  useLogin, 
  useLogout, 
  useRegister,
  UserProfile,
  LoginInput,
  RegisterInput,
  setAuthTokenGetter,
  setErrorReporter,
  setTokenRefreshFn,
} from "@workspace/api-client-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { setSentryUser, clearSentryUser, Sentry } from "@/lib/sentry";
import { identifyUser, resetUser, trackEvent } from "@/lib/posthog";
import { refreshAccessToken } from "@/lib/auth-refresh";

setAuthTokenGetter(() => localStorage.getItem("token"));
setErrorReporter((error, context) => {
  Sentry.captureException(error, {
    tags: { apiMethod: context.method, apiUrl: context.url, statusCode: context.status },
  });
});

setTokenRefreshFn(refreshAccessToken);

type AuthContextType = {
  user: UserProfile | null;
  isLoading: boolean;
  login: (data: LoginInput) => Promise<void>;
  register: (data: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  // Set token dynamically in localStorage to ensure the generated client sends it
  // Assuming @workspace/api-client-react uses a fetch interceptor that checks localStorage
  const { data: user, isLoading: isUserLoading, isPending: isUserPending, refetch, error } = useGetMe({
    query: {
      retry: (failureCount, err) => {
        if (failureCount < 1 && err && typeof err === "object" && "status" in err && (err as { status: number }).status === 401) return true;
        return false;
      },
      refetchOnWindowFocus: false,
    }
  });

  useEffect(() => {
    if (user) {
      setSentryUser({ id: user.id, role: user.role, email: user.email });
      identifyUser({ id: user.id, role: user.role, email: user.email, gymId: user.gymId });
    } else if (!isUserLoading) {
      clearSentryUser();
      resetUser();
    }
  }, [user, isUserLoading]);

  const loginMutation = useLogin();
  const registerMutation = useRegister();
  const logoutMutation = useLogout();

  const handleLogin = async (data: LoginInput) => {
    try {
      const res = await loginMutation.mutateAsync({ data });
      localStorage.setItem('token', res.token);
      await refetch();
      trackEvent("user_logged_in", { role: res.user.role });
      toast({ title: "Welcome back!", description: "Successfully logged in." });
      
      // Route based on role
      if (res.user.role === 'SUPERADMIN') setLocation('/superadmin');
      else if (res.user.role === 'ADMIN') setLocation('/admin/dashboard');
      else if (res.user.role === 'TRAINER') setLocation('/trainer/dashboard');
      else setLocation('/dashboard');
      
    } catch (err: any) {
      toast({ 
        title: "Login failed", 
        description: err.message || "Invalid credentials", 
        variant: "destructive" 
      });
      throw err;
    }
  };

  const handleRegister = async (data: RegisterInput) => {
    try {
      const res = await registerMutation.mutateAsync({ data });
      localStorage.setItem('token', res.token);
      await refetch();
      trackEvent("user_registered", { role: res.user?.role });
      toast({ title: "Account created!", description: "Welcome to OPEN MAT." });
      setLocation('/dashboard');
    } catch (err: any) {
      toast({ 
        title: "Registration failed", 
        description: err.message || "Could not create account", 
        variant: "destructive" 
      });
      throw err;
    }
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      localStorage.removeItem('token');
      await refetch();
      setLocation('/');
      toast({ title: "Logged out", description: "You have been logged out." });
    }
  };

  return (
    <AuthContext.Provider value={{
      user: user || null,
      isLoading: isUserPending,
      login: handleLogin,
      register: handleRegister,
      logout: handleLogout,
      refreshUser: async () => { await refetch(); },
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
